import { useEffect, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';

const DEFAULT_SETTINGS = { notifyEmail: 'conflictchecks@aalrr.com', copySubmitter: true, senderUpn: '', senderName: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';

export default function AdminConflictRouting() {
  const { data: stored, loading } = useDoc('settings/conflictRouting');
  const { run, busy, error, saved } = useCallable();
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!dirty) {
      setSettings(stored ? structuredClone(stored) : DEFAULT_SETTINGS);
    }
  }, [stored, dirty]);

  if (loading || !settings) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const patch = (changes) => {
    setDirty(true);
    setSettings({ ...settings, ...changes });
    setValidationError('');
  };

  const save = async () => {
    const notifyEmail = settings.notifyEmail.trim();
    if (!EMAIL_RE.test(notifyEmail)) {
      setValidationError('Enter a valid email address.');
      return;
    }
    const senderUpn = String(settings.senderUpn || '').trim();
    if (senderUpn && !EMAIL_RE.test(senderUpn)) {
      setValidationError('The sending mailbox must be a valid email address, or left blank.');
      return;
    }

    setValidationError('');
    // adminUpdateSettings REPLACES the document, so every field has to be sent, not just the
    // one that changed. Listing them individually rather than spreading `settings` keeps a stale
    // key from a previous shape out of the saved document.
    const result = await run('adminUpdateSettings', {
      docId: 'conflictRouting',
      value: {
        notifyEmail,
        copySubmitter: settings.copySubmitter !== false,
        senderUpn: String(settings.senderUpn || '').trim(),
        senderName: String(settings.senderName || '').trim(),
      },
    });
    if (result) setDirty(false);
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Conflicts notification</h2>
          <CallStatus busy={busy} error={error} saved={saved} />
        </div>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Conflict check requests are sent to this one address. Test mode, when enabled, still
          redirects the outgoing message to its test inbox.
        </p>

        <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
          Conflicts notification email address
          <input
            className={`${field} mt-1`}
            type="email"
            value={settings.notifyEmail || ''}
            onChange={(event) => patch({ notifyEmail: event.target.value })}
            onBlur={() => {
              if (settings.notifyEmail && !EMAIL_RE.test(settings.notifyEmail.trim())) {
                setValidationError('Enter a valid email address.');
              }
            }}
            placeholder="conflictchecks@aalrr.com"
            aria-describedby={validationError ? 'conflict-routing-validation' : undefined}
          />
        </label>
        {validationError && (
          <p id="conflict-routing-validation" className="mt-2 text-sm text-red-700 dark:text-red-300" role="alert">
            {validationError}
          </p>
        )}

        <label className="mt-4 flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={settings.copySubmitter !== false}
            onChange={(event) => patch({ copySubmitter: event.target.checked })}
          />
          <span>
            Copy the person who submitted it
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              They are cc’d on the request and receive the same spreadsheet, so they have a record of
              what they asked to be searched. Turning this off leaves them with no copy at all.
            </span>
          </span>
        </label>

        <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Who the request appears to come from
          </h3>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            Leave both blank to send from the firm’s usual intake mailbox under its own name.
          </p>

          <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
            Name shown in the recipient’s From column
            <input
              className={`${field} mt-1`}
              value={settings.senderName || ''}
              onChange={(event) => patch({ senderName: event.target.value })}
              placeholder="AALRR Conflicts"
            />
            <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">
              A label only. The address is unchanged, so this needs no permission from IT.
            </span>
          </label>

          <label className="mt-3 block text-xs text-slate-600 dark:text-slate-300">
            Send from this mailbox instead
            <input
              className={`${field} mt-1`}
              type="email"
              value={settings.senderUpn || ''}
              onChange={(event) => patch({ senderUpn: event.target.value })}
              placeholder="conflictchecks@aalrr.com"
            />
            <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">
              <strong>It must be a real mailbox.</strong> A distribution list or mail-enabled group
              cannot send mail, whatever permissions it is given — even though mail can be sent
              <em>to</em> it perfectly well. The app also has to be allowed to send as the mailbox,
              which means adding it to the Exchange application access policy.
              <br />
              If either is wrong the send fails and appears in <strong>Admin → Mail queue</strong>
              with the reason, rather than going missing — but it will not retry on its own. Send a
              test before relying on it.
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="mt-4 rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save conflicts notification settings
        </button>
      </section>
    </div>
  );
}
